import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-artifact-list>` says about itself.
 *
 * Two lines per row, and that is the whole design. The real name on the first line, what kind of
 * thing it is plus its human label underneath in smaller type. With several hundred artifacts that
 * second line is the difference between a usable picker and an alphabetical wall — `AccountTrigger`
 * and `AccountTriggerHandler` are indistinguishable at a glance, and the type tells them apart.
 *
 * That is also why this is a list of buttons rather than a `<select>`: the platform's option is a
 * single string with nowhere to put a qualifier, which the specification names as a gap. A row that
 * is a real element can hold two lines, be styled, and carry one accessible name reading both.
 *
 * Unsaved work is counted over the WHOLE set rather than the filtered one, because unsaved work a
 * filter has hidden is exactly the unsaved work somebody is about to forget about.
 */
const docs: ComponentDocs = {
  aliases: ['class list', 'file list', 'metadata picker', 'artifact picker', 'code browser'],

  answers: [
    'pick a code artifact out of a long list',
    'tell two similarly named classes apart',
    'find every trigger in the environment',
    'see which files have unsaved changes before leaving',
  ],

  placements: ['appPage'],

  whenToUse:
    'Whenever somebody chooses one thing out of many where the NAME ALONE is not enough to tell them ' +
    'apart — which, past a couple of dozen artifacts, is always.',

  insteadUse: [
    {
      name: 'PicklistField',
      when:
        'the choices are short, few, and unambiguous from their labels. A dropdown option is a single ' +
        'string with nowhere to put a qualifier, which is the whole reason this component exists.',
    },
    {
      name: 'DataTable',
      when: 'the artifacts are being COMPARED across several columns rather than chosen between.',
    },
  ],

  inputs: [
    {
      name: 'artifacts',
      type: 'json',
      description:
        'What is there: `{ name, type, storedType?, label?, dirty? }[]`. `storedType` is shown beside ' +
        'the friendly type when the two differ — "Apex Class (ApexClass)" — because both names get ' +
        'used and making somebody transliterate is a tax paid on every line.',
      default: [],
    },
    {
      name: 'type',
      attribute: 'type',
      type: 'text',
      description:
        'Show only artifacts of this type. The filter’s CONTENTS are the types actually present — ' +
        'offering a type this environment has none of is offering an empty result.',
      fallback: 'every type, when the attribute is absent or empty',
    },
    {
      name: 'search',
      attribute: 'search',
      type: 'text',
      description: 'Narrows by name, label or type. Intersects with the type filter rather than replacing it.',
    },
    {
      name: 'selected',
      attribute: 'selected',
      type: 'text',
      description: 'Which artifact is current, by name. Reflected when a row is chosen, so it can be linked to.',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say when nothing matches.',
      default: 'No artifacts match.',
    },
  ],

  events: [
    {
      name: 'caos-artifact-open',
      when: 'A row is pressed.',
      carries: '`{ artifact }` — the whole artifact, not just its name, so a host need not look it up again.',
    },
  ],

  state: [
    { name: 'visibleArtifacts', type: 'CaosArtifact[]', description: 'The artifacts surviving both filters.' },
    {
      name: 'dirtyArtifacts',
      type: 'CaosArtifact[]',
      description: 'Everything with unsaved changes, across the WHOLE set — a filter never hides this count.',
    },
  ],

  parts: [
    { part: 'list', is: 'The outer column.' },
    { part: 'filters', is: 'The type and search row.' },
    { part: 'type', is: 'The type dropdown.' },
    { part: 'search', is: 'The search box.' },
    { part: 'summary', is: 'The count line, including how many have unsaved changes.' },
    { part: 'rows', is: 'The bordered list of rows.' },
    { part: 'row', is: 'One artifact’s row.' },
    { part: 'name', is: 'The first line — the real name.' },
    { part: 'qualifier', is: 'The second line — the type and label.' },
    { part: 'dirty', is: 'The unsaved-changes mark.', when: 'The artifact has unsaved changes.' },
  ],

  accessibility:
    'Each row carries ONE accessible name reading both lines and the unsaved state — "AccountTrigger ' +
    '— Apex Trigger (ApexTrigger) — unsaved changes" — because a screen reader announcing only the ' +
    'first line gets exactly the alphabetical wall this component exists to avoid. Rows are real ' +
    '`<button>` elements in a `role="list"`, so they are reachable and pressable by keyboard in ' +
    'order, and the current one is marked `aria-current`. The summary is a `role="status"` live ' +
    'region and always states how many of the whole set survived the filters.',

  examples: [
    {
      key: 'two-lines',
      label: 'Why the second line is there',
      summary:
        'Three artifacts whose names differ by a suffix. The qualifier is what tells a trigger from ' +
        'its handler from the test that covers it, at a glance rather than by reading carefully.',
      placement: {
        component: 'caos-artifact-list',
        inputs: {
          selected: 'AccountTrigger',
          artifacts: [
            { name: 'AccountTrigger', type: 'Apex Trigger', storedType: 'ApexTrigger', dirty: true },
            { name: 'AccountTriggerHandler', type: 'Apex Class', storedType: 'ApexClass' },
            { name: 'AccountTriggerTest', type: 'Apex Class', storedType: 'ApexClass', label: 'Account trigger coverage' },
            { name: 'InvoiceSummary', type: 'Page', storedType: 'ApexPage', label: 'Invoice summary' },
          ],
        },
      },
      stage: { needsWidth: 520 },
    },
    {
      key: 'filtered',
      label: 'Filtered to one type',
      summary:
        'The summary says two of four survived, and still counts the unsaved change on the row the ' +
        'filter has hidden — which is exactly the one somebody is about to forget about.',
      placement: {
        component: 'caos-artifact-list',
        inputs: {
          type: 'Apex Class',
          artifacts: [
            { name: 'AccountTrigger', type: 'Apex Trigger', storedType: 'ApexTrigger', dirty: true },
            { name: 'AccountTriggerHandler', type: 'Apex Class', storedType: 'ApexClass' },
            { name: 'AccountTriggerTest', type: 'Apex Class', storedType: 'ApexClass' },
            { name: 'QuoteSummary', type: 'Page', storedType: 'ApexPage' },
          ],
        },
      },
      stage: { needsWidth: 520 },
    },
  ],
};

export default docs;
